import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, Users, ShieldAlert, FileSearch, Activity, Mail, Shuffle, ExternalLink, FileDown } from "lucide-react";
import { TerminalBreakdown } from "@/components/TerminalBreakdown";
import { TerminalManagerDashboard } from "@/components/TerminalManagerDashboard";
import { ViolationsWidget } from "@/components/ViolationsWidget";
import { FilingsWidget } from "@/components/fleet/FilingsWidget";
import { HazmatWidget } from "@/components/company/HazmatWidget";
import { FixItWidget } from "@/components/audit/FixItWidget";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";
import { PacketButton } from "@/pages/CustomerProfile";
import { EmailPacketDialog } from "@/components/EmailPacketDialog";
import { PacketShareHistory, PacketSchedulePanel } from "@/components/PacketSharePanel";
import { HealthScoreBadge } from "@/components/HealthScoreBadge";

function riskBadge(status) {
  if (status === "expired")
    return <Badge className="bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-50">Expired</Badge>;
  return <Badge className="bg-amber-50 text-amber-800 border border-amber-300 hover:bg-amber-50">Expiring soon</Badge>;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [shareKey, setShareKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: summary } = await api.get("/dashboard/summary");
        if (!cancelled) setData(summary);
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="p-8 text-slate-500">Loading dashboard…</div>;
  if (user?.role === "terminal_manager") return <TerminalManagerDashboard user={user} />;
  if (!data) return <div className="p-8">No data</div>;

  const kpis = [
    { label: "Total Drivers", value: data.total_drivers, icon: Users, testid: "kpi-total-drivers", tone: "border-l-4 border-l-blue-600 bg-blue-50/40", ic: "text-blue-600" },
    { label: "Active Drivers", value: data.active_drivers, icon: Activity, testid: "kpi-active-drivers", tone: "border-l-4 border-l-emerald-600 bg-emerald-50/40", ic: "text-emerald-600" },
    { label: "In Random Pool", value: data.in_random_pool, icon: ShieldAlert, testid: "kpi-random-pool", tone: "border-l-4 border-l-violet-600 bg-violet-50/40", ic: "text-violet-600" },
    { label: "Pending Reports", value: data.pending_reports, icon: FileSearch, testid: "kpi-pending-reports", tone: "border-l-4 border-l-amber-500 bg-amber-50/40", ic: "text-amber-600" },
  ];
  const isAdmin = user?.role === "admin";

  const runAlerts = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/alerts/run");
      toast.success(`${data.sent} alert(s) emailed · ${data.skipped} already sent · ${data.failed} failed`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSending(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]" data-testid="dashboard-page">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <div className="tag-mono text-blue-700 mb-1">OVERVIEW</div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold font-heading text-slate-900">
            Welcome back, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-slate-600 mt-1">Consortium status and compliance risk at a glance.</p>
          {!isAdmin && <div className="mt-3"><HealthScoreBadge company={user.company || user.name} /></div>}
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={runAlerts} disabled={sending} data-testid="dashboard-send-alerts-button">
              <Mail className="h-4 w-4 mr-2" />{sending ? "Sending…" : "Send expiration alerts now"}
            </Button>
          )}
          {isAdmin && (
            <Button variant="outline" onClick={async () => { try { const { data: r } = await api.post("/onboarding/nudges/run"); toast.success(`${r.sent} setup nudge(s) emailed`); } catch (err) { toast.error(formatApiError(err)); } }} data-testid="dashboard-send-nudges-button">Send setup nudges</Button>
          )}
          <Link to="/billing"><Button variant="outline" data-testid="dashboard-billing-button">{isAdmin ? `Billing · $${(data.month_billing_total || 0).toFixed(2)} this month` : "Billing"}</Button></Link>
          {!isAdmin && <PacketButton customerId={user.id} testid="dashboard-packet-button" />}
          {!isAdmin && <EmailPacketDialog customerId={user.id} company={user.company || user.name} onSent={() => setShareKey((k) => k + 1)} />}
          <Link to="/reports"><Button className="bg-blue-700 hover:bg-blue-800" data-testid="dashboard-request-report-button">Request Report</Button></Link>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {kpis.map((k) => (
          <Card key={k.label} className={`p-5 ${k.tone}`} data-testid={k.testid}>
            <div className="flex items-center justify-between mb-3">
              <div className="tag-mono text-slate-500">{k.label}</div>
              <k.icon className={`h-4 w-4 ${k.ic}`} />
            </div>
            <div className="text-3xl font-bold font-mono text-slate-900">{k.value}</div>
          </Card>
        ))}
      </div>

      <a href="https://results.thesaferteam.com/login/" target="_blank" rel="noopener noreferrer" className="block mb-6 group" data-testid="manage-randoms-card">
        <Card className="p-6 bg-slate-950 text-slate-100 border-slate-800 hover:border-blue-500 transition-colors">
          <div className="flex items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-md bg-blue-600 flex items-center justify-center shrink-0"><Shuffle className="h-6 w-6 text-white" /></div>
              <div>
                <div className="tag-mono text-blue-300">RANDOM TESTING PROGRAM</div>
                <h2 className="text-xl font-bold font-heading">Manage Randoms</h2>
                <p className="text-sm text-slate-300 mt-1">Open the Safer Highways randoms management portal to view selections, results and pool status.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm font-semibold text-blue-300 group-hover:text-white shrink-0">Open login <ExternalLink className="h-4 w-4" /></div>
          </div>
        </Card>
      </a>

      {!isAdmin && <TerminalBreakdown customerId={user?.id} />}

      {isAdmin && data.company_overview && (
        <Card className="mb-6 overflow-hidden border-t-4 border-t-blue-600" data-testid="company-overview-widget">
          <div className="px-6 py-4 border-b border-blue-100 bg-blue-50/60 flex items-center justify-between">
            <div>
              <div className="tag-mono text-blue-700">COMPANY OVERVIEW</div>
              <h2 className="text-xl font-bold font-heading text-slate-900">Expirations &amp; certificates per customer</h2>
            </div>
            <Link to="/customers" className="text-sm text-blue-700 hover:underline" data-testid="company-overview-manage-link">Manage customers →</Link>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr className="text-left"><th className="px-6 py-2 font-medium">Company</th><th className="px-3 py-2 font-medium text-right">Drivers</th><th className="px-3 py-2 font-medium text-right">Vehicles</th><th className="px-3 py-2 font-medium text-right">Certificates</th><th className="px-3 py-2 font-medium text-right">Expiring / expired</th><th className="px-3 py-2 font-medium text-right">At-risk drivers</th><th className="px-3 py-2 font-medium text-right">Open requests</th><th className="px-6 py-2" /></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.company_overview.length === 0 && <tr><td colSpan={7} className="px-6 py-6 text-center text-slate-500">No customers yet — invite one from the Customers page.</td></tr>}
              {data.company_overview.map((c) => (
                <tr key={c.customer_id} data-testid={`company-overview-row-${c.customer_id}`}>
                  <td className="px-6 py-2.5"><Link to={`/customers/${c.customer_id}`} className="font-medium text-slate-900 hover:text-blue-700 hover:underline" data-testid={`company-overview-profile-${c.customer_id}`}>{c.company}</Link><div className="text-xs text-slate-500 flex items-center gap-2">{c.customer_name}{c.setup && c.setup.done < c.setup.total && <span className="rounded-full bg-fuchsia-100 text-fuchsia-800 border border-fuchsia-300 px-2 py-0.5 font-mono text-[10px]" data-testid={`overview-setup-${c.customer_id}`}>Setup {c.setup.done}/{c.setup.total}</span>}</div></td>
                  <td className="px-3 py-2.5 text-right font-mono">{c.drivers}</td>
                  <td className="px-3 py-2.5 text-right font-mono"><Link to={`/fleet?customer=${c.customer_id}`} className={c.vehicles ? "text-blue-700 hover:underline" : "text-slate-400"} data-testid={`overview-vehicles-${c.customer_id}`}>{c.vehicles ?? 0}</Link></td>
                  <td className="px-3 py-2.5 text-right font-mono"><Link to={`/certificates?customer=${c.customer_id}`} className="text-blue-700 hover:underline">{c.certificates}</Link></td>
                  <td className={`px-3 py-2.5 text-right font-mono ${c.total_expirations > 0 ? "text-rose-700 font-semibold" : "text-slate-500"}`} data-testid={`company-expirations-${c.customer_id}`}>{c.total_expirations}</td>
                  <td className={`px-3 py-2.5 text-right font-mono ${c.at_risk_drivers > 0 ? "text-amber-700" : "text-slate-500"}`}>{c.at_risk_drivers}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{c.open_requests}</td>
                  <td className="px-6 py-2.5 text-right"><Link to={`/drivers?customer=${c.customer_id}`} className="text-xs text-blue-700 hover:underline">Drivers →</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {!isAdmin && <FixItWidget />}
      {!isAdmin && <OnboardingChecklist />}
      <FilingsWidget isAdmin={isAdmin} />
      <HazmatWidget isAdmin={isAdmin} />
      <div className="mb-6"><ViolationsWidget /></div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* At-risk drivers */}
        <Card className="p-6 border-t-4 border-t-rose-500 bg-gradient-to-b from-rose-50/70 to-white" data-testid="at-risk-drivers-widget">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="tag-mono text-rose-700">AT-RISK DRIVERS</div>
              <h2 className="text-xl font-bold font-heading text-slate-900">Expiring & expired licenses</h2>
            </div>
            <AlertTriangle className="h-5 w-5 text-rose-600" />
          </div>
          {data.at_risk_drivers.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">All drivers are compliant. </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {data.at_risk_drivers.map((row) => (
                <div key={row.driver.id} className="py-3 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">
                      {row.driver.first_name} {row.driver.last_name}
                    </div>
                    <div className="text-xs text-slate-500 font-mono">
                      CDL: {row.driver.license_number} · {row.driver.license_state}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {row.risks.map((r) => (
                      <div key={r.field} className="flex items-center gap-2 text-xs">
                        <span className="text-slate-500">{r.label}</span>
                        {riskBadge(r.status)}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Certificate expirations */}
        <Card className="p-6 border-t-4 border-t-amber-500 bg-gradient-to-b from-amber-50/70 to-white" data-testid="expirations-tracker-widget">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="tag-mono text-amber-700">EXPIRATION ALERTS</div>
              <h2 className="text-xl font-bold font-heading text-slate-900">Certificates &amp; documents</h2>
              <p className="text-xs text-slate-500 mt-1">Email reminders go out automatically 30, 14 and 7 days before expiry{isAdmin ? " and in the Monday digest" : ""}.</p>
            </div>
            {data.cert_expiring.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {data.cert_expiring.filter((c) => c.expiry_status === "expired").length > 0 && <Badge className="bg-rose-50 text-rose-700 border border-rose-200" data-testid="cert-expired-count">{data.cert_expiring.filter((c) => c.expiry_status === "expired").length} expired</Badge>}
                <Badge className="bg-amber-50 text-amber-800 border border-amber-300" data-testid="cert-expiring-count">{data.cert_expiring.filter((c) => c.expiry_status === "expiring").length} due soon</Badge>
              </div>
            )}
          </div>
          {data.cert_expiring.length === 0 && !data.vehicle_expiring?.length ? (
            <div className="text-sm text-slate-500 py-8 text-center">Nothing expiring soon.</div>
          ) : data.cert_expiring.length === 0 ? null : (
            <div className="divide-y divide-slate-100">
              {data.cert_expiring.map((c) => (
                <Link key={c.id} to={`/certificates${isAdmin && c.owner_customer_id ? `?customer=${c.owner_customer_id}` : ""}`} className="py-3 flex items-center justify-between hover:bg-slate-50 -mx-2 px-2 rounded" data-testid={`cert-alert-${c.id}`}>
                  <div>
                    <div className="font-medium text-slate-900">{c.cert_type}<span className="text-slate-500 font-normal"> · {c.driver_name || "Company-level"}</span></div>
                    <div className="text-xs font-mono text-slate-500">{isAdmin && <span className="text-blue-700">{c.company} · </span>}{c.expiry_status === "expired" ? `Expired ${-c.days_left}d ago` : `Expires in ${c.days_left}d`} · {c.expiry_date}</div>
                  </div>
                  {riskBadge(c.expiry_status)}
                </Link>
              ))}
            </div>
          )}
          {data.vehicle_expiring?.length > 0 && (
            <div className="mt-4 pt-3 border-t border-slate-200" data-testid="vehicle-expiring-list">
              <div className="tag-mono text-slate-500 mb-1">Vehicles · registrations &amp; documents ({data.vehicle_expiring_count})</div>
              <div className="divide-y divide-slate-100">
                {data.vehicle_expiring.map((x) => (
                  <Link key={x.id} to={`/fleet${isAdmin ? `?customer=${x.customer_id}` : ""}`} className="py-2.5 flex items-center justify-between hover:bg-slate-50 -mx-2 px-2 rounded" data-testid={`vehicle-alert-${x.vehicle_id}`}>
                    <div>
                      <div className="font-medium text-slate-900">{x.unit}<span className="text-slate-500 font-normal"> · {x.label}</span></div>
                      <div className="text-xs font-mono text-slate-500">{isAdmin && <span className="text-blue-700">{x.company} · </span>}{x.vehicle}{x.vehicle ? " · " : ""}{x.expiry_status === "expired" ? `Expired ${-x.days_left}d ago` : `Expires in ${x.days_left}d`} · {x.expiry_date}</div>
                    </div>
                    {riskBadge(x.expiry_status)}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>
      {!isAdmin && (
        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          <PacketSchedulePanel customerId={user.id} />
          <PacketShareHistory customerId={user.id} refreshKey={shareKey} />
        </div>
      )}
    </div>
  );
}
